import { defaultDealStages } from "../root/defaultConfiguration";
import type {
  DealStageConfig,
  DealStageCriterion,
  StageCriterionCode,
  StagePlacement,
} from "../types";
import { isPosiComplete, type PosiAnswers } from "./posi";
import { ALMIKIA_STAGE_VALUES, POSI_GATED_STAGES } from "./stages";

/**
 * Almikia Epic EP, DATA CONTRACT EP frontend data layer: the single shared,
 * pure module every later EP ticket (TASK-103 to TASK-108) imports instead
 * of re-implementing. Three things live here: (1) the criteria registry
 * keyed by `criterion_code`, (2) the effective-stage resolver of DATA
 * CONTRACT EP (8), and (3) the "is this stage gated by this criterion?"
 * rule with the E2 fallback of DATA CONTRACT EP (4). Pure and linear in the
 * number of stages/criteria rows (the ticket's own performance requirement)
 * -- no I/O, no React, safe to import from both the FakeRest provider and
 * (later) UI components.
 */

/** One evaluated criterion result: `missingItems` is `[]` exactly when `met`. */
export interface CriterionEvaluationResult {
  met: boolean;
  /** Missing item labels, in DATA CONTRACT EP (3) order. */
  missingItems: string[];
}

/** A criterion's registry entry: its UI name plus its pure evaluator. */
export interface StageCriterionDefinition {
  code: StageCriterionCode;
  /** DATA CONTRACT EP (3) UI name, e.g. for a stage's criteria list. */
  label: string;
  evaluate: (
    answers: Partial<PosiAnswers> | null | undefined,
  ) => CriterionEvaluationResult;
}

const isBlank = (value: string | null | undefined): boolean =>
  value == null || value.trim() === "";

/**
 * DATA CONTRACT EP (3) items, in the exact order missing-item messages must
 * use. Reuses {@link isPosiComplete}'s semantics (same 4 fields, same
 * trim-then-blank check) for the `met` decision.
 */
const POSI_ITEMS: ReadonlyArray<{ field: keyof PosiAnswers; label: string }> = [
  { field: "posi_intent", label: "intención" },
  { field: "posi_budget", label: "presupuesto" },
  { field: "posi_payment_method", label: "forma de pago" },
  { field: "posi_decision_maker", label: "quién decide" },
];

/**
 * The criteria registry, keyed by `criterion_code` ('posi_complete' only in
 * EP). Every later EP ticket importing a criterion's evaluator or UI name
 * must go through this registry, never re-declare the POSI item list.
 */
export const STAGE_CRITERIA_REGISTRY: Record<
  StageCriterionCode,
  StageCriterionDefinition
> = {
  posi_complete: {
    code: "posi_complete",
    label: "Las 4 preguntas POSI respondidas",
    evaluate: (answers) => ({
      met: isPosiComplete(answers),
      missingItems: POSI_ITEMS.filter(({ field }) =>
        isBlank(answers?.[field]),
      ).map(({ label }) => label),
    }),
  },
};

/**
 * DATA CONTRACT EP (4): a stage key with NO `deal_stage_criteria` row for
 * `criterionCode` is gated exactly when it is in the E2 default list; an
 * existing row decides otherwise (`enabled: true` = gated, `false` = not
 * gated). A stage value that is neither a configured key nor an E2 key
 * (legacy) is never gated. This is the fail-safe both SQL and TS must apply
 * while configuration is loading, failed to load, or was never seeded.
 */
export function isStageGatedByCriterion(
  stageKey: string,
  criterionCode: StageCriterionCode,
  criteria: readonly DealStageCriterion[],
): boolean {
  const row = criteria.find(
    (criterion) =>
      criterion.stage_key === stageKey &&
      criterion.criterion_code === criterionCode,
  );
  if (row) {
    return row.enabled;
  }
  return (
    criterionCode === "posi_complete" &&
    (POSI_GATED_STAGES as readonly string[]).includes(stageKey)
  );
}

/** One row of the resolved, effective stage list (DATA CONTRACT EP (8)). */
export interface EffectiveDealStage {
  /** The `deals.stage` value this entry describes. */
  value: string;
  label: string;
  placement: StagePlacement;
  position: number;
  archived: boolean;
  isSystem: boolean;
  targetHours: number | null;
  requiredActivities: string[];
}

const PLACEMENT_ORDER: Record<StagePlacement, number> = {
  inbox: 0,
  kanban: 1,
  special: 2,
};

/** E2's own placement for each of the 10 system keys (the fallback of DATA CONTRACT EP (4)). */
const E2_PLACEMENT_BY_KEY: Record<string, StagePlacement> = {
  prospecto: "inbox",
  contacto_inicial: "kanban",
  discovery: "kanban",
  showing: "kanban",
  cierre: "kanban",
  administracion: "kanban",
  venta_cerrada: "special",
  cerrado_perdido: "special",
  codigo_azul: "special",
  seguimiento_futuro: "special",
};

const E2_LABEL_BY_KEY: Record<string, string> = Object.fromEntries(
  defaultDealStages.map((stage) => [stage.value, stage.label]),
);

/**
 * DATA CONTRACT EP (8): every configured row plus the E2 default for any
 * missing system key, ordered `inbox`, then `kanban`, then `special`, each
 * by `(position, key)`. `dealStages[0]` is always `'prospecto'` because it
 * is the only `'inbox'`-placement entry. With the seeded configuration (or
 * with no configuration rows at all) this returns exactly today's
 * `defaultDealStages` (values, labels, order) with E2 placements: the E2
 * default `position` below (`(index + 1) * 10`) matches the DATA CONTRACT
 * EP SEED's own positions (10, 20, …, 100) exactly.
 */
export function resolveEffectiveDealStages(
  configs: readonly DealStageConfig[],
): EffectiveDealStage[] {
  const configByKey = new Map(configs.map((config) => [config.key, config]));
  const seenKeys = new Set<string>();

  const systemEntries: EffectiveDealStage[] = ALMIKIA_STAGE_VALUES.map(
    (key, index) => {
      seenKeys.add(key);
      const config = configByKey.get(key);
      return {
        value: key,
        label: config?.label ?? E2_LABEL_BY_KEY[key] ?? key,
        placement: config?.placement ?? E2_PLACEMENT_BY_KEY[key],
        position: config?.position ?? (index + 1) * 10,
        archived: config?.archived_at != null,
        isSystem: config?.is_system ?? true,
        targetHours: config?.target_hours ?? null,
        requiredActivities: config?.required_activities ?? [],
      };
    },
  );

  const customEntries: EffectiveDealStage[] = configs
    .filter((config) => !seenKeys.has(config.key))
    .map((config) => ({
      value: config.key,
      label: config.label,
      placement: config.placement,
      position: config.position,
      archived: config.archived_at != null,
      isSystem: config.is_system,
      targetHours: config.target_hours ?? null,
      requiredActivities: config.required_activities ?? [],
    }));

  return [...systemEntries, ...customEntries].sort((a, b) => {
    const placementDiff =
      PLACEMENT_ORDER[a.placement] - PLACEMENT_ORDER[b.placement];
    if (placementDiff !== 0) return placementDiff;
    if (a.position !== b.position) return a.position - b.position;
    return a.value.localeCompare(b.value);
  });
}

/** Offered as a move target (kanban drop, stage picker, readiness target): non-archived only. */
export function getOfferableDealStages(
  configs: readonly DealStageConfig[],
): EffectiveDealStage[] {
  return resolveEffectiveDealStages(configs).filter((stage) => !stage.archived);
}
